/**
 * Select
 *
 * A single-choice dropdown: a button trigger with a chevron that opens a
 * portal-rendered listbox. Focus stays on the trigger; the active option is
 * announced through aria-activedescendant (WAI-ARIA select-only combobox).
 *
 * Keyboard: ArrowUp/ArrowDown/Home/End move, Enter/Space choose, Escape and
 * Tab close, a printable key jumps to the next option that starts with it.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  selectKeyAction,
  selectedOptionLabel,
  typeaheadIndex,
  type SelectOption,
} from './select-state';

export type { SelectOption } from './select-state';

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  disabled?: boolean;
}

const LISTBOX_MAX_HEIGHT = 240;

export function Select({
  value,
  onValueChange,
  options,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  className,
  disabled = false,
}: SelectProps) {
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const height = Math.min(LISTBOX_MAX_HEIGHT, options.length * 32 + 8);
      const wantsBelow = rect.bottom + 4 + height <= window.innerHeight;
      const top = wantsBelow ? rect.bottom + 4 : Math.max(8, rect.top - height - 4);
      setPos({ top, left: rect.left, width: rect.width });
    };
    place();

    const isInside = (target: EventTarget | null) => target instanceof Node && (
      Boolean(listboxRef.current?.contains(target)) || Boolean(triggerRef.current?.contains(target))
    );
    const closeOnOutside = (event: MouseEvent) => {
      if (!isInside(event.target)) setOpen(false);
    };
    const closeOnScroll = (event: Event) => {
      if (!isInside(event.target)) setOpen(false);
    };
    const closeOnResize = () => setOpen(false);

    document.addEventListener('mousedown', closeOnOutside);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnResize);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnResize);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${listboxId}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, listboxId]);

  const choose = (index: number) => {
    const option = options[index];
    setOpen(false);
    if (option && option.value !== value) onValueChange(option.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const action = selectKeyAction(event.key, {
      open,
      activeIndex,
      selectedIndex,
      count: options.length,
    });
    if (action === null) {
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const match = typeaheadIndex(options, event.key, open ? activeIndex : selectedIndex);
        if (match >= 0) {
          event.preventDefault();
          if (open) setActiveIndex(match);
          else choose(match);
        }
      }
      return;
    }
    if (action.type !== 'close' || event.key !== 'Tab') event.preventDefault();
    if (action.type === 'open') {
      setActiveIndex(action.index);
      setOpen(true);
    } else if (action.type === 'move') {
      setActiveIndex(action.index);
    } else if (action.type === 'choose') {
      choose(action.index);
    } else {
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        disabled={disabled}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          setActiveIndex(Math.max(0, selectedIndex));
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'flex h-7 min-w-0 items-center justify-between gap-1 rounded-md border border-surface-200 bg-white px-2 text-left text-xs text-surface-700',
          'hover:bg-surface-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'dark:border-surface-700 dark:bg-surface-900 dark:text-surface-200 dark:hover:bg-surface-800',
          className,
        )}
      >
        <span className="truncate">{selectedOptionLabel(options, value)}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-surface-500" aria-hidden="true" />
      </button>

      {open && pos && createPortal(
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          style={{ top: pos.top, left: pos.left, minWidth: pos.width, maxHeight: LISTBOX_MAX_HEIGHT }}
          className="fixed z-50 overflow-y-auto rounded-md border border-surface-200 bg-white py-1 shadow-lg dark:border-surface-700 dark:bg-surface-900"
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <div
                key={option.value}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={selected}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs text-surface-700 dark:text-surface-200',
                  index === activeIndex && 'bg-surface-100 dark:bg-surface-800',
                  selected && 'font-medium text-surface-900 dark:text-surface-50',
                )}
              >
                <Check className={cn('h-3.5 w-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                <span className="truncate">{option.label}</span>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
